import { dialogAtom } from '@/features/shared/state/Dialog/atom'
import {
  type DialogSessionData,
  type DynamicDialogComponent,
  type DynamicDialogData,
} from '@/features/shared/state/Dialog/types'
import { useSetAtom } from 'jotai'
import { useCallback } from 'react'

// oxlint-disable-next-line no-restricted-imports -- this needs to be updated to use useAtom or useAtomValue
import { getJotaiStore } from '@/features/shared/state'
import { useDialogContext } from '@/features/shared/components/DialogContainer'
import { useRetailerContext } from '@/features/shared/context/RetailerContext'
import { useCMSContext } from '@/features/cms/components/CMS/CMSContext'
import { setupReactModal } from '@/lib/setupReactModal'
import { noop } from '@/features/shared/utils/noop'

let hasSetupModal = false

export const useHandleDialog = () => {
  const dialogContext = useDialogContext()
  const setDialog = useSetAtom(dialogAtom)
  const retailerContext = useRetailerContext()
  // Carry store-override contexts into the dialog's portal; the viewer's own
  // context falls back automatically.
  const dialogRetailerContext = retailerContext?.propagateToDialogs ? retailerContext : undefined
  const cmsData = useCMSContext()

  const openDialog = useCallback(
    async <T extends DynamicDialogComponent>(component: T, ...dialogData: DynamicDialogData<T>) => {
      if (!hasSetupModal) {
        hasSetupModal = true
        setupReactModal()
      }
      const data = dialogData[0]
      const handleOpenDialog = () =>
        dialogContext.setDialogState({
          component,
          props: { ...data, dialogRetailerContext, cmsData },
        })

      // this pattern is only supported in the pages router. dynamic import behaves
      // differently in the app router and the preload method is not available
      if (
        typeof component.render === 'function' &&
        typeof component.render.preload === 'function'
      ) {
        await component.render.preload().catch(noop)
      }
      handleOpenDialog()
    },
    [dialogRetailerContext, dialogContext, cmsData],
  )

  const setLegacySessionData = (data: Partial<DialogSessionData>) => {
    setDialog((prev) => ({
      ...prev,
      legacySessionData: {
        ...prev.legacySessionData,
        ...data,
      },
    }))
  }

  const getLegacySessionData = () => {
    const jotaiStore = getJotaiStore()
    const dialog = jotaiStore.get(dialogAtom)
    return dialog.legacySessionData
  }

  return {
    openDialog,
    closeDialog: dialogContext.closeDialog,
    setLegacySessionData,
    getLegacySessionData,
  } as const
}
